import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useId,
} from 'react';

import * as RadixSelect from '@radix-ui/react-select';
import type {
  SelectProps as RadixSelectRootProps,
  SelectContentProps as RadixSelectContentProps,
  SelectItemProps as RadixSelectItemProps,
} from '@radix-ui/react-select';
import type { IconComponent } from '@repo/ui-icons';
import IconCheckmark from '@repo/ui-icons/IconCheckmark';
import IconChevronDown from '@repo/ui-icons/IconChevronDown';
import cn from 'classnames';

import type { BaseComponentProps } from '../../../types/common';
import {
  DropdownItem,
  type DropdownItemProps,
  type DropdownItemSizeProps,
} from '../../private/DropdownItem';
import { H4, H5, H6, H7 } from '../Typography';

import styles from './Select.module.css';

interface SelectSharedProps extends DropdownItemSizeProps {
  id?: string;
}

// Create a context for size
const SelectSharedPropsContext = createContext<SelectSharedProps>({
  size: 's',
});

const labelComponents = {
  xs: H7,
  s: H6,
  m: H5,
};

export interface SelectRootProps
  extends PropsWithChildren<DropdownItemSizeProps>,
    BaseComponentProps {
  /**
   * Label of the select.
   */
  label?: string;
  /**
   * Whether the select is disabled.
   */
  disabled?: boolean;
  /**
   * Value of the select.
   * This is used by radix to determine the selected value (consider as an `id`).
   */
  value?: RadixSelectRootProps['value'];
  /**
   * Event handler called when the user selects an item.
   */
  onValueChange?: RadixSelectRootProps['onValueChange'];
  /**
   * Whether the select is open.
   */
  open?: RadixSelectRootProps['open'];
  /**
   * Event handler called when the user opens or closes the select.
   */
  onOpenChange?: RadixSelectRootProps['onOpenChange'];
}

/**
 * The root component for the select.
 */
const SelectRoot: React.FC<SelectRootProps> = ({
  children,
  disabled,
  value,
  onValueChange,
  open,
  onOpenChange,
  label,
  size = 's',
  dataTestId,
}) => {
  const LabelComponent = labelComponents[size];
  const id = useId();

  return (
    <SelectSharedPropsContext.Provider value={{ id, size }}>
      <div
        className={cn(styles.select, styles[`size-${size}`])}
        data-testid={dataTestId}
      >
        {label && (
          <label aria-disabled={disabled} className={styles.label} htmlFor={id}>
            <LabelComponent as="span" color={disabled ? 'disabled' : 'default'}>
              {label}
            </LabelComponent>
          </label>
        )}
        <RadixSelect.Root
          disabled={disabled}
          open={open}
          value={value}
          onOpenChange={onOpenChange}
          onValueChange={onValueChange}
        >
          {children}
        </RadixSelect.Root>
      </div>
    </SelectSharedPropsContext.Provider>
  );
};

export interface SelectTriggerProps {
  /**
   * Placeholder text to display when the select is empty.
   */
  placeholder?: string;
}

const triggerTextComponents = {
  xs: H6,
  s: H6,
  m: H5,
  l: H4,
};

/**
 * The trigger component for the select.
 */
const SelectTrigger: React.FC<PropsWithChildren<SelectTriggerProps>> = ({
  children,
  placeholder,
}) => {
  const { id, size = 's' } = useContext(SelectSharedPropsContext);
  const TriggerTextComponent = triggerTextComponents[size];

  return (
    <RadixSelect.Trigger className={styles.trigger} id={id}>
      <TriggerTextComponent textOverflowEllipsis color="inherit">
        <RadixSelect.Value placeholder={placeholder}>
          {children}
        </RadixSelect.Value>
      </TriggerTextComponent>
      <RadixSelect.Icon className={styles['chevron-icon']}>
        <IconChevronDown color="currentColor" />
      </RadixSelect.Icon>
    </RadixSelect.Trigger>
  );
};

export interface SelectContentProps
  extends PropsWithChildren<BaseComponentProps> {
  /**
   * The position of the select content.
   */
  position?: RadixSelectContentProps['position'];
  /**
   * The side of the select content.
   */
  side?: RadixSelectContentProps['side'];
  /**
   * The side offset of the select content.
   */
  sideOffset?: RadixSelectContentProps['sideOffset'];
  /**
   * The align of the select content.
   */
  align?: RadixSelectContentProps['align'];
  /**
   * The align offset of the select content.
   */
  alignOffset?: RadixSelectContentProps['alignOffset'];
}

/**
 * The content component for the select.
 */
const SelectContent: React.FC<SelectContentProps> = ({
  children,
  sideOffset = 8,
  side = 'bottom',
  dataTestId,
  ...rest
}) => {
  return (
    <RadixSelect.Portal>
      <RadixSelect.Content
        className={styles.content}
        data-testid={dataTestId}
        side={side}
        sideOffset={sideOffset}
        sticky="always"
        {...rest}
      >
        <RadixSelect.Viewport className={styles.viewport}>
          {children}
        </RadixSelect.Viewport>
      </RadixSelect.Content>
    </RadixSelect.Portal>
  );
};

export interface SelectItemProps
  extends Omit<DropdownItemProps, 'size'>,
    PropsWithChildren {
  /**
   * Event handler called when the user selects an item.
   */
  onSelect?: (e: Event) => void;
  /**
   * Event handler called when the user click on item.
   */
  onClick?: (e: Event) => void;
  /**
   * Value of the item.
   */
  value: RadixSelectItemProps['value'];
  /**
   * Icon component to display on the left side of the label.
   */
  icon?: IconComponent;
}

/**
 * The item component for the select.
 */
const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ children, value, icon, ...props }, ref) => {
    const { size = 's' } = useContext(SelectSharedPropsContext);

    return (
      <RadixSelect.Item
        ref={ref}
        asChild
        disabled={props.disabled}
        value={value}
      >
        <DropdownItem {...props} className={styles['select-item']} size={size}>
          {icon && <DropdownItem.Icon icon={icon} />}
          {children}
          <RadixSelect.ItemIndicator asChild>
            <DropdownItem.Icon icon={IconCheckmark} />
          </RadixSelect.ItemIndicator>
        </DropdownItem>
      </RadixSelect.Item>
    );
  }
);
SelectItem.displayName = 'SelectItem';

export interface SelectItemTextProps {
  /**
   * Icon component to display on the left side of the label.
   */
  icon?: IconComponent;
  /**
   * Label of the item.
   */
  label: string;
}

/**
 * The text component for the select item. This part is placed inside the trigger when the item is selected.
 */
const SelectItemText: React.FC<SelectItemTextProps> = ({ icon, label }) => {
  const { size = 's' } = useContext(SelectSharedPropsContext);

  return (
    <RadixSelect.ItemText data-item-text>
      <DropdownItem.Body icon={icon} label={label} size={size} />
    </RadixSelect.ItemText>
  );
};

SelectItemText.displayName = 'SelectItemText';

const SelectSeparator: React.FC = () => {
  return <RadixSelect.Separator className={styles.separator} />;
};

export interface SelectGroupProps extends PropsWithChildren {
  /**
   * Label of the group.
   */
  label: string;
}

const SelectGroup: React.FC<SelectGroupProps> = ({
  children,
  label,
  ...rest
}) => {
  const { size = 's' } = useContext(SelectSharedPropsContext);

  return (
    <RadixSelect.Group {...rest}>
      <RadixSelect.Label>
        <DropdownItem className={styles['group-label-item']} size={size}>
          <DropdownItem.Body label={label} size={size} />
        </DropdownItem>
      </RadixSelect.Label>
      {children}
    </RadixSelect.Group>
  );
};

export const Select = {
  Root: SelectRoot,
  Trigger: SelectTrigger,
  Content: SelectContent,
  Item: SelectItem,
  ItemText: SelectItemText,
  Group: SelectGroup,
  GroupSeparator: SelectSeparator,
};
